import { useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useStore } from '@/app/StoreContext';
import { LobbyView } from '@/components/room/LobbyView';
import { RoundView } from '@/components/round/RoundView';
import { RoundStartSequence } from '@/components/round/RoundStartSequence';
import { RoundResultsView } from '@/components/round/RoundResultsView';
import { MatchFinalView } from '@/components/round/MatchFinalView';
import { Spinner } from '@/components/ui/Spinner';
import { runtime } from '@/platform/runtime';
import { showBackButton, hideBackButton } from '@/platform/backButton';
import { ScreenTransition, ROOM_STATUS_ORDER, useScreenDirection } from '@/components/transitions/ScreenTransition';

export const RoomPage = observer(function RoomPage() {
  const { code } = useParams<{ code: string }>();
  const nav = useNavigate();
  const { auth, room, round } = useStore();
  const prefersReducedMotion = useReducedMotion() ?? false;
  const direction = useScreenDirection(room.room?.status ?? 'WaitingForPlayers', ROOM_STATUS_ORDER);

  useEffect(() => {
    if (!runtime.isMiniApp) return;
    showBackButton(() => nav('/'));
    return () => hideBackButton();
  }, []);

  useEffect(() => {
    if (code) document.title = `Комната ${code} — Сквозь`;
    return () => { document.title = 'Сквозь'; };
  }, [code]);

  useEffect(() => {
    if (!code) return;
    if (!auth.isAuthenticated) {
      nav(`/?join=${code}`, { replace: true });
      return;
    }

    const gen = room.subscribe(code);
    return () => room.unsubscribe(gen);
  }, [code, auth.isAuthenticated]);

  const focusWordInput = useCallback(() => {
    document.querySelector<HTMLInputElement>('[data-word-input]')?.focus();
  }, []);

  const handleSequenceDone = useCallback(() => {
    round.clearJustStarted();
  }, [round]);

  if (room.isLoading && !room.room) return <Spinner />;
  if (room.error) return <p>Ошибка: {room.error}</p>;
  if (!room.room) return null;

  const status = room.room.status;
  const showSequence = status === 'InGame' && round.justStarted && !!round.letters;
  const roundLabel =
    round.totalRounds > 1
      ? `Раунд ${round.roundIndex + 1} / ${round.totalRounds}`
      : undefined;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <ScreenTransition motionKey={status} direction={direction}>
        {status === 'WaitingForPlayers' && <LobbyView />}
        {status === 'InGame' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <RoundView />
            <AnimatePresence onExitComplete={focusWordInput}>
              {showSequence && (
                <motion.div
                  key="round-start-seq"
                  style={{ position: 'absolute', inset: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                  <RoundStartSequence
                    letters={round.letters as [string, string, string]}
                    roundLabel={roundLabel}
                    onDone={handleSequenceDone}
                    reducedMotion={prefersReducedMotion}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        {status === 'BetweenRounds' && <RoundResultsView />}
        {status === 'Finished' && <MatchFinalView />}
        {status === 'Archived' && <p>Комната закрыта.</p>}
      </ScreenTransition>
    </div>
  );
});
